/**
 * The theatre's title bar: what this movement is, whose it is, and what may be done to it.
 *
 * ── There is no „Dodaj do dnia", and that is a decision rather than an omission ──────────────
 * Owner decision D7, 17 September 2026: it is cut on `/cwiczenia` because there is no day to add
 * to. The button returns when the builder hosts this same theatre, which is the whole reason the
 * card was designed as a lightbox over a list rather than a screen of its own.
 *
 * ── The ownership action ────────────────────────────────────────────────────────────────────
 * A LIBRARY movement offers „Dostosuj materiały", which is the one thing a trainer may do to
 * somebody else's exercise: attach their own links as an overlay. It opens the editor in the rail.
 *
 * A movement the trainer OWNS offers „Edytuj" and a `…` menu with Duplikuj / Zmień okładkę / Usuń —
 * the creator's four acts (PR 2). Edytuj and Duplikuj open `features/create-exercise` over the card
 * (a NESTED dialog, so Escape closes the wizard and leaves the card); Zmień okładkę opens the file
 * chooser and uploads straight to `PUT /exercise/{id}/cover`; Usuń asks first. The hidden file input
 * lives here, inside the popup, because a control outside a modal dialog's subtree is unreachable.
 *
 * ── Ember appears NOWHERE on this surface ───────────────────────────────────────────────────
 * The ownership action is a ghost, the sparkle under the hero is a grey pill, the close square is a
 * ghost. The accent marks a real action or a real state; opening a card to read it is neither, and
 * the one ember on this screen is the header's `+` four hundred pixels behind the scrim.
 */

import { useRef } from 'react';
import { Menu } from '@base-ui/react/menu';
import { useTranslation } from 'react-i18next';

import { exerciseTypeColour } from '~/entities/exercise-library';
import { ExerciseTypeGlyph } from '~/entities/exercise-library';
import ghost from '~/shared/ui/ghostButton.module.css';
import { LinkGlyph } from '~/entities/exercise-library';
import menuStyles from '~/shared/ui/MenuSurface.module.css';

import { COVER_ACCEPT } from '~/shared/media/coverFile';

import type { TheatreSubject } from '../lib/useTheatre';

import {
  CloseGlyph,
  CopyGlyph,
  MoreGlyph,
  PencilGlyph,
  UploadGlyph,
} from './ExerciseLibraryIcons';
import { CheckGlyph, TrashGlyph } from '~/shared/ui/FormGlyphs';
import styles from './ExerciseTheatre.module.css';

interface TheatreBarProps {
  readonly subject: TheatreSubject;
  /** „Dostosuj materiały" — opens the editor in the rail. */
  readonly onCustomiseMaterials: () => void;
  readonly onClose: () => void;
  /** The four acts on an OWN movement. Unused on a library one, where they are not drawn. */
  readonly onEdit: () => void;
  readonly onDuplicate: () => void;
  readonly onChangeCover: (file: File) => void;
  readonly onDelete: () => void;
  /** The ⋯ trigger, so the dialogs its menu raises can hand focus back to it. */
  readonly moreRef: React.RefObject<HTMLButtonElement | null>;
  /**
   * „Oznacz jako sprawdzone" (board D, R17): offered on an own movement still marked for review,
   * beside „Edytuj", which never takes the mark off. `checked` is this card's mark just taken off,
   * `failed` a press that could not take it off.
   */
  readonly review: { readonly onMark: () => void; readonly marking: boolean; readonly checked: boolean; readonly failed: boolean };
}

export function TheatreBar({
  subject,
  onCustomiseMaterials,
  onClose,
  onEdit,
  onDuplicate,
  onChangeCover,
  onDelete,
  moreRef,
  review,
}: TheatreBarProps) {
  const { t } = useTranslation();
  const { row } = subject;
  const coverInput = useRef<HTMLInputElement>(null);

  return (
    <div className={styles.bar}>
      {/* The type's GLYPH and the WORD, in the type's own hue — the glyph at 13px beside the word,
        * not the 30px wash square the table's TYP column draws. The specimen drew a dot here; the
        * owner replaced it with the glyph everywhere the mark appears (D17, 18 September 2026). */}
      <span className={styles.typeMark} style={exerciseTypeColour(row.typeKey)}>
        <ExerciseTypeGlyph type={row.typeKey} className={styles.typeGlyph} />
        {row.typeLabel}
      </span>

      {/* The card's own heading. The screen's `h1` is „Ćwiczenia" and the preview panel behind this
        * is an `h2` on the same name, so the card's heading is an `h2` too — it is not nested under
        * anything on this surface.
        *
        * NOT a `Dialog.Title`, and that is the one place this card departs from the obvious shape.
        * `Dialog.Title` exists to set `aria-labelledby` on the popup, and `aria-labelledby`
        * OUTRANKS `aria-label` — so a title element here silently shadows the name the popup
        * carries, whatever that name is. The bar used to BE the title element, which is how the
        * dialog came to announce itself as „Siłowe · <name> · Dostosuj materiały · Esc". The name
        * is `web:exerciseLibrary.theatre.label` on the popup; this is the heading it is about. */}
      {/* `tabIndex={-1}` and the data hook: after a save opens this card, focus lands HERE — the
        * movement just made — rather than on the popup or the walk. */}
      <h2 className={styles.name} tabIndex={-1} data-theatre-heading>
        {row.name}
      </h2>
      {subject.ownership === 'own' && (
        <span className={styles.badge}>{t('exerciseLibrary.list.yourBadge')}</span>
      )}
      {subject.detail.needsReview && <span className={styles.review}>{t('web:exerciseLibrary.review.status')}</span>}

      <span className={styles.spacer} />

      {/* Board D's confirmation, neutral: said once in the screen's live region, and standing here
        * until the card closes. A failure stands in the same place. */}
      {review.checked && !subject.detail.needsReview && (
        <span className={styles.checked}>
          <CheckGlyph className={styles.checkedGlyph} />
          {t('web:exerciseLibrary.review.checked')}
        </span>
      )}
      {/* A press that failed says so in the same place, until the next press or the card closing. */}
      {review.failed && subject.detail.needsReview && !review.marking && (
        <span className={styles.checked}>{t('web:exerciseLibrary.review.markFailed')}</span>
      )}

      {subject.actions.editable ? (
        <>
          {subject.detail.needsReview && (
            <button
              type="button"
              className={`${String(ghost.ghost)} ${String(styles.barButton)}`}
              aria-disabled={review.marking ? 'true' : undefined}
              aria-busy={review.marking ? 'true' : undefined}
              onClick={review.marking ? undefined : review.onMark}
            >
              {t('web:exerciseLibrary.review.markChecked')}
            </button>
          )}
          <button
            type="button"
            className={`${String(ghost.ghost)} ${String(styles.barButton)}`}
            onClick={onEdit}
          >
            <PencilGlyph className={styles.barGlyph} />
            {t('exerciseLibrary.overflowMenu.edit')}
          </button>
          <Menu.Root>
            <Menu.Trigger
              ref={moreRef}
              className={`${String(ghost.ghost)} ${String(styles.barSquare)}`}
              aria-label={t('exerciseLibrary.overflowMenu.openA11y')}
            >
              <MoreGlyph className={styles.barGlyph} />
            </Menu.Trigger>
            <Menu.Portal>
              <Menu.Positioner className={menuStyles.positioner} sideOffset={6} align="end">
                <Menu.Popup className={menuStyles.popup}>
                  <Menu.Item className={menuStyles.item} onClick={onDuplicate}>
                    <CopyGlyph className={styles.itemGlyph} />
                    <span className={menuStyles.itemLabel}>
                      {t('web:exerciseLibrary.theatre.duplicate')}
                    </span>
                  </Menu.Item>
                  <Menu.Item
                    className={menuStyles.item}
                    onClick={() => {
                      coverInput.current?.click();
                    }}
                  >
                    <UploadGlyph className={styles.itemGlyph} />
                    <span className={menuStyles.itemLabel}>
                      {t('web:exerciseLibrary.theatre.changeCover')}
                    </span>
                  </Menu.Item>
                  <Menu.Item className={menuStyles.item} onClick={onDelete}>
                    <TrashGlyph className={styles.itemGlyph} />
                    <span className={menuStyles.itemLabel}>
                      {t('exerciseLibrary.overflowMenu.delete')}
                    </span>
                  </Menu.Item>
                </Menu.Popup>
              </Menu.Positioner>
            </Menu.Portal>
          </Menu.Root>
          <input
            ref={coverInput}
            type="file"
            accept={COVER_ACCEPT}
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              // Cleared, so choosing the same picture twice still fires a change.
              event.target.value = '';
              if (file !== undefined) onChangeCover(file);
            }}
          />
        </>
      ) : (
        <button
          type="button"
          className={`${String(ghost.ghost)} ${String(styles.barButton)}`}
          onClick={onCustomiseMaterials}
        >
          <LinkGlyph className={styles.barGlyph} />
          {t('web:exerciseLibrary.theatre.customiseMaterials')}
        </button>
      )}

      {/* The close square with the Esc key drawn UNDER it (owner decision D22, 18 September 2026:
        * „daj przycisk Esc pod X”), so the bar reads Edytuj · ⋯ · ✕ with nothing wedged between.
        * The key is decorative — the square carries the whole act and its name, and a listener who
        * cannot see the hint still has Escape. */}
      <span className={styles.closeStack}>
        <button
          type="button"
          className={`${String(ghost.ghost)} ${String(styles.barSquare)}`}
          aria-label={t('common.close')}
          onClick={onClose}
        >
          <CloseGlyph className={styles.barGlyph} />
        </button>
        <span className={`${String(styles.kbd)} ${String(styles.kbdBelow)}`} aria-hidden>
          {t('web:exerciseLibrary.theatre.escKey')}
        </span>
      </span>
    </div>
  );
}
